import { useAuth } from "@/hooks/use-auth";
import { useEffect, useRef, useState } from "react";
import { loadDraftFromLocal, useFormContext } from "../context/FormProvider";
import { fetchPostProperty } from "../fetchRawUnit";
import { splitRawUnitToSteps } from "../splitRawUnitToSteps";
import type { FormState, StepKey } from "../types/form";
import { useGetSellerGroupData } from "./useGraphQLQueries";
interface UseSellFlowDataReturn {
  isInitializing: boolean;
  initializationError: Error | null;
}
function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const output: Record<string, unknown> = {
    ...target
  };
  Object.keys(source).forEach(key => {
    const targetVal = target[key];
    const sourceVal = source[key];
    if (targetVal && typeof targetVal === "object" && !Array.isArray(targetVal) && sourceVal && typeof sourceVal === "object" && !Array.isArray(sourceVal)) {
      output[key] = deepMerge(targetVal as Record<string, unknown>, sourceVal as Record<string, unknown>);
    } else {
      output[key] = sourceVal;
    }
  });
  return output;
}
export function useSellFlowData(rawUnitId: string | null, sellerGroupId: string | null, isBLOGroup: boolean, sellerUnitId?: string | null): UseSellFlowDataReturn {
  const {
    isAuthenticated
  } = useAuth();
  const {
    updateStepData,
    mergeStepData
  } = useFormContext();
  const [isInitializing, setIsInitializing] = useState(true);
  const [initializationError, setInitializationError] = useState<Error | null>(null);
  // Hook to get seller group data when needed
  const {
    data: groupDataResult,
    loading
  } = useGetSellerGroupData({
    groupId: sellerGroupId || null,
    isAuthenticated
  });

  // Extract seller group data for dependency tracking
  const sellerGroupData = groupDataResult?.seller?.getSellerGroup;

  // Track initialization status to prevent redundant re-fetches/overwrites
  const hasInitializedRef = useRef(false);

  // Reset initialization ONLY if the stable property/URL IDs change.
  // DO NOT reset when sellerGroupId changes - that happens during relationship selection
  // and would cause the form to reload and overwrite user input.
  const [apiData, setApiData] = useState<Record<string, unknown> | null>(null);
  const [localData, setLocalData] = useState<FormState | null>(null);
  const [isLocalLoading, setIsLocalLoading] = useState(true);
  const [isApiLoading, setIsApiLoading] = useState(false);

  // Reset initialization ONLY if the stable property/URL IDs change.
  useEffect(() => {
    // When the rawUnitId or sellerUnitId changes, we need to re-initialize.
    hasInitializedRef.current = false;
    setIsInitializing(true);
  }, [rawUnitId, sellerUnitId]);

  // Effect 1: Fetch API data when stable IDs change
  useEffect(() => {
    if (!(rawUnitId && sellerGroupId && isAuthenticated)) {
      setApiData(null);
      if (rawUnitId && isAuthenticated && !sellerGroupId) {
        // We have a unit but no group yet, wait for group
        return;
      }
      return;
    }
    const fetchData = async () => {
      setIsApiLoading(true);
      setInitializationError(null);
      try {
        const rawUnit = await fetchPostProperty({
          id: rawUnitId,
          groupId: sellerGroupId,
          isAuthenticated
        });

        // Check if the fetched unit belongs to the user's selected group
        if (rawUnit && rawUnit.groupId !== sellerGroupId) {
          throw new Error("Unauthorized: Unit does not belong to the selected group");
        }
        if (rawUnitId && !rawUnit && isAuthenticated) {
          throw new Error("Unauthorized or Unit not found.");
        }
        setApiData(rawUnit);
      } catch (error) {
        setInitializationError(error instanceof Error ? error : new Error("Failed to fetch property data"));
        setApiData(null);
      } finally {
        setIsApiLoading(false);
      }
    };
    fetchData();
  }, [rawUnitId, sellerGroupId, isAuthenticated]);

  // Effect 2: Load local data when IDs change
  useEffect(() => {
    const loadLocal = async () => {
      setIsLocalLoading(true);
      const data = await loadDraftFromLocal(rawUnitId || sellerUnitId || sellerGroupId || null);
      setLocalData(data);
      setIsLocalLoading(false);
    };
    loadLocal();
  }, [rawUnitId, sellerUnitId, sellerGroupId]);

  // Effect 3: Sync combined data to form context
  useEffect(() => {
    // Wait for all data sources to be ready
    if (loading || isLocalLoading || isApiLoading) return;
    const syncData = async () => {
      try {
        // Only re-initialize if not already done for these IDs
        if (hasInitializedRef.current) {
          setIsInitializing(false);
          return;
        }
        setInitializationError(null);

        // 1. Prepare Group Data
        let relationship = apiData ? apiData.relationship : "";
        let groupData;
        if (sellerGroupData) {
          groupData = {
            id: sellerGroupData.id,
            name: sellerGroupData.name,
            entityType: sellerGroupData.entityType,
            companyName: sellerGroupData.companyName,
            officeAddress: sellerGroupData.officeAddress,
            gstNo: sellerGroupData.gstNo,
            projects: sellerGroupData.projects || []
          };
          if (!relationship) {
            relationship = sellerGroupData.type === "individual" ? "owner" : sellerGroupData.type;
          }
        }

        // 2. Combine with API data
        const combinedData = {
          ...(groupData || {}),
          ...(apiData || {}),
          relationship
        };

        // 3. Determine if we have valid API-side data to initialize with
        const apiStepData = (rawUnitId || isBLOGroup) && Object.keys(combinedData).length > 2 // length > 2 ensures we have more than just default relationship/empty objects
        ? splitRawUnitToSteps(combinedData) : null;
        if (apiStepData && Object.keys(apiStepData).length > 0) {
          // Merge API data with local data
          // Only use local data if we haven't initialized or if we have a specific ID
          const effectiveLocalData = !hasInitializedRef.current || rawUnitId ? localData : null;
          Object.entries(apiStepData).forEach(([key, apiValue]) => {
            if (key !== "schemaVersion") {
              const stepKey = key as StepKey;
              const localValue = effectiveLocalData?.[stepKey] ?? {};
              const mergedValue = deepMerge(localValue as Record<string, unknown>, apiValue as Record<string, unknown>);

              // If we are already initialized and have no ID (creation flow),
              // use mergeStepData to avoid replacing user-entered name/phone.
              if (hasInitializedRef.current && !rawUnitId) {
                mergeStepData(stepKey, mergedValue);
              } else {
                updateStepData(stepKey, mergedValue);
              }
            }
          });
        } else {
          // Fallback: Use only local data
          if (localData) {
            Object.entries(localData).forEach(([key_0, value]) => {
              if (key_0 !== "schemaVersion") {
                updateStepData(key_0 as StepKey, value);
              }
            });
          }
        }
        hasInitializedRef.current = true;
        setIsInitializing(false);
      } catch (error_0) {
        setInitializationError(error_0 instanceof Error ? error_0 : new Error("Failed to initialize form data"));
        setIsInitializing(false);
      }
    };
    syncData();
  }, [loading, isLocalLoading, isApiLoading, apiData, localData, sellerGroupData, rawUnitId, isBLOGroup, updateStepData, mergeStepData]);
  return {
    isInitializing,
    initializationError
  };
}